/**
 * billing.ts — สัญญาของ webhook เกตเวย์ชำระเงิน (paywall)
 *
 * webhook แปลง event ของเกตเวย์ (PromptPay/Omise/2C2P/…) → รูปแบบกลางนี้
 * แล้วอัปเดต plan/plan_status ของบัญชี. โค้ด adapter เฉพาะเกตเวย์ (map payload +
 * header ลายเซ็น) เสียบตรง endpoint นี้ — ตัว endpoint เป็นกลาง ยืนยันด้วย HMAC secret
 */
import { z } from 'zod';
import { uuid } from './schemas.js';

/** event กลางหลังผ่าน adapter ของเกตเวย์ */
export const billingWebhookEvent = z
  .object({
    /** id ของ event จากเกตเวย์ (ไว้ audit/กันซ้ำ) */
    eventId: z.string().trim().min(1, 'ต้องมี eventId').max(200),
    /** ชนิด event จากเกตเวย์ (ไว้ audit/routing) */
    type: z.string().trim().min(1).max(120),
    /** ระบุบัญชี: ตรง ๆ ด้วย accountId หรือผ่าน billingCustomerId ที่ผูกไว้ */
    accountId: uuid.optional(),
    billingCustomerId: z.string().trim().min(1).max(200).optional(),
    /** แพ็กเกจใหม่ (ถ้าเปลี่ยน) */
    plan: z.enum(['trial', 'starter', 'growth', 'business']).optional(),
    /** สถานะบิลลิ่งใหม่ (บังคับ) */
    planStatus: z.enum(['trialing', 'active', 'past_due', 'canceled']),
    /** สิ้นสุดรอบบิลปัจจุบัน (ISO) */
    currentPeriodEnd: z.string().trim().max(40).optional(),
    billingSubscriptionId: z.string().trim().min(1).max(200).optional(),
  })
  .refine((e) => Boolean(e.accountId ?? e.billingCustomerId), {
    message: 'ต้องมี accountId หรือ billingCustomerId เพื่อระบุบัญชี',
    path: ['accountId'],
  });

export type BillingWebhookEvent = z.infer<typeof billingWebhookEvent>;
